import { useState, useEffect, useRef } from "react";
import { X, Dumbbell, Activity, Zap, Heart, Brain, Clock, Timer, Trash2, GripVertical } from "lucide-react";

// Helper: determine block type and colors (similar to calendar cards)
function getBlockStyle(label) {
  const l = String(label || "").toLowerCase();

  if (/warm|échauff|warmup|activation/i.test(l)) {
    return {
      icon: Heart,
      bgLight: "bg-orange-500",
      bgDark: "bg-orange-600",
      accentClass: "bg-orange-400/30",
    };
  }
  if (/force|strength|muscul|gym|squat|bench/i.test(l)) {
    return {
      icon: Dumbbell,
      bgLight: "bg-rose-500",
      bgDark: "bg-rose-600",
      accentClass: "bg-rose-400/30",
    };
  }
  if (/tactique|tactic|video|analyse|possession/i.test(l)) {
    return {
      icon: Brain,
      bgLight: "bg-violet-500",
      bgDark: "bg-violet-600",
      accentClass: "bg-violet-400/30",
    };
  }
  if (/intensif|intense|hiit|sprint|interval|high.?intensity/i.test(l)) {
    return {
      icon: Zap,
      bgLight: "bg-amber-500",
      bgDark: "bg-amber-600",
      accentClass: "bg-amber-400/30",
    };
  }
  if (/récup|recup|recovery|cool|retour|stretch/i.test(l)) {
    return {
      icon: Heart,
      bgLight: "bg-emerald-500",
      bgDark: "bg-emerald-600",
      accentClass: "bg-emerald-400/30",
    };
  }
  if (/endur|aerobi|cardio|continu/i.test(l)) {
    return {
      icon: Activity,
      bgLight: "bg-cyan-500",
      bgDark: "bg-cyan-600",
      accentClass: "bg-cyan-400/30",
    };
  }
  // Default: generic training block
  return {
    icon: Activity,
    bgLight: "bg-sky-500",
    bgDark: "bg-sky-600",
    accentClass: "bg-sky-400/30",
  };
}

export default function TimelineBlock({
  block,
  pxPerMin,
  rowHeight,
  isDarkMode,
  isActive,
  isSelected,
  onPointerDownMove,
  onPointerDownResize,
  onRemove,
  onClick,
}) {
  const [contextMenu, setContextMenu] = useState(null);
  const blockRef = useRef(null);

  const left = (block.startMin || 0) * pxPerMin;
  const width = Math.max(40, (block.durationMin || 0) * pxPerMin);
  const restWidth = (block.restDurationMin || 0) * pxPerMin;
  const blockHeight = rowHeight - 32;

  const style = getBlockStyle(block.label);
  const IconComponent = style.icon;

  // Determine display mode based on width
  const isCompact = width < 80;
  const isMedium = width < 140;

  // Close context menu when clicking outside
  useEffect(() => {
    if (!contextMenu) return;
    const handleClickOutside = (e) => {
      setContextMenu(null);
    };
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("contextmenu", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("contextmenu", handleClickOutside);
    };
  }, [contextMenu]);

  // Handle right-click
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Menu dimensions (approximate)
    const menuWidth = 160;
    const menuHeight = 40;
    
    // Calculate position, ensuring menu stays within viewport
    let x = e.clientX;
    let y = e.clientY;
    
    if (x + menuWidth > viewportWidth) {
      x = viewportWidth - menuWidth - 8;
    }
    if (y + menuHeight > viewportHeight) {
      y = viewportHeight - menuHeight - 8;
    }
    
    setContextMenu({ x, y });
  };

  // Handle delete
  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu(null);
    onRemove?.();
  };

  return (
    <>
      <div
        ref={blockRef}
        className={`absolute top-4 rounded-xl select-none overflow-hidden shadow-md transition-all cursor-grab active:cursor-grabbing ${
          isDarkMode ? style.bgDark : style.bgLight
        } text-white ${
          isActive ? "ring-2 ring-white shadow-lg scale-[1.02] z-10" : ""
        } ${
          isSelected ? "ring-2 ring-emerald-400 ring-offset-2 ring-offset-gray-900" : ""
        } hover:shadow-lg hover:brightness-110 group`}
        style={{ left, width, height: blockHeight }}
        onPointerDown={(e) => {
          // Don't start drag on right-click
          if (e.button === 2) return;
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.setPointerCapture(e.pointerId);
          onPointerDownMove(e);
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
        onContextMenu={handleContextMenu}
        title={`${block.label || "Block"} · ${block.durationMin}min\nClick to select · Right-click to delete · Drag to move`}
      >
        {/* Accent stripe on left */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${style.accentClass}`} />

        {/* Delete button - visible on hover */}
        <button
          className="absolute top-1 right-1 rounded-full p-1 bg-black/30 hover:bg-red-500 transition-all opacity-0 group-hover:opacity-100 z-20"
          onPointerDown={(e) => {
            // Prevent drag from starting
            e.stopPropagation();
          }}
          onClick={handleDelete}
          title="Supprimer le bloc"
        >
          <X size={12} className="text-white" />
        </button>

        {/* Resize handle - only for duration, not rest */}
        <div
          className="absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize flex items-center justify-center opacity-0 hover:opacity-100 group-hover:opacity-60 transition-opacity z-20"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.setPointerCapture(e.pointerId);
            onPointerDownResize?.(e);
          }}
          title="Resize duration"
        >
          <GripVertical size={12} className="text-white/80" />
        </div>

        {isCompact ? (
          /* Compact: just icon */
          <div className="flex items-center justify-center h-full pl-2">
            <IconComponent size={16} className="text-white/90" />
          </div>
        ) : isMedium ? (
          /* Medium: icon + label */
          <div className="flex items-center gap-2 p-2 pl-3 h-full">
            <div className={`p-1.5 rounded-lg ${style.accentClass} flex-shrink-0`}>
              <IconComponent size={14} className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold truncate leading-tight">
                {block.label || "Block"}
              </p>
              <div className="flex items-center gap-1 mt-0.5 text-white/80">
                <Timer size={10} />
                <span className="text-[10px] font-medium">{block.durationMin}′</span>
              </div>
            </div>
          </div>
        ) : (
          /* Full: all info */
          <div className="flex items-start gap-2 p-2 pl-3 h-full">
            <div className={`p-1.5 rounded-lg ${style.accentClass} flex-shrink-0`}>
              <IconComponent size={16} className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold truncate leading-tight">
                {block.label || "Block"}
              </p>
              <div className="flex items-center gap-2 mt-1 text-white/80">
                <div className="flex items-center gap-1">
                  <Clock size={10} />
                  <span className="text-[10px] font-medium">
                    {Math.floor((block.startMin || 0) / 60)}h{String((block.startMin || 0) % 60).padStart(2, "0")}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Timer size={10} />
                  <span className="text-[10px] font-medium">{block.durationMin}′</span>
                </div>
              </div>
              {block.intensity && (
                <div className="mt-1">
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/20 font-medium">
                    {block.intensity}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Rest block (if any) */}
      {restWidth > 0 && (
        <div
          className={`absolute top-4 rounded-xl select-none overflow-hidden border-2 border-dashed transition-opacity ${
            isDarkMode 
              ? "bg-gray-800/40 border-gray-600/50" 
              : "bg-gray-100/60 border-gray-300/50"
          } ${isActive || isSelected ? "opacity-70" : "opacity-50"}`}
          style={{ 
            left: left + width, 
            width: restWidth, 
            height: blockHeight 
          }}
          title="Rest period"
        >
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Timer size={14} className={`mx-auto ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
              <p className={`text-[10px] font-medium mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                {block.restDurationMin}′ rest
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-50 py-1 rounded-lg shadow-xl border min-w-[140px]"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
            borderColor: isDarkMode ? "#374151" : "#e5e7eb",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors ${
              isDarkMode
                ? "text-red-400 hover:bg-red-500/20"
                : "text-red-600 hover:bg-red-50"
            }`}
            onClick={handleDelete}
          >
            <Trash2 size={14} />
            Supprimer
          </button>
        </div>
      )}
    </>
  );
}

export { getBlockStyle };
